"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { jwtDecode } from "jwt-decode";
import AlertDialog from "./AlertDialog"; // Adjust the import path as needed

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#4b5563", // bg-gray-600
    borderColor: state.isFocused ? "#f97316" : "#6b7280", // orange-400, gray-500
    color: "#e5e7eb", // text-gray-200
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none", // orange-400
    "&:hover": { borderColor: "#f97316" }, // orange-400
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb", // text-gray-200
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151", // gray-600, gray-700
    color: state.isSelected ? "#f97316" : "#e5e7eb", // orange-400, gray-200
    "&:hover": { backgroundColor: "#6b7280" }, // gray-500
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151", // bg-gray-700
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    border: "1px solid #4b5563", // border-gray-600
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb", // text-gray-200
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af", // text-gray-400
  }),
};

const API = "https://erp-xe9u.onrender.com/api";

export default function MaterialsTab({ project, setProject }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("orders");
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderData, setOrderData] = useState({
    purchaseOrder: "",
    vendor: "",
    poNumber: "",
    items: [],
    shippingMethod: "Delivery",
    deliveryInstructions: "",
    attachments: [],
    materialOrderNumber: "",
  });
  const [vendors, setVendors] = useState([]);
  const [materialOrders, setMaterialOrders] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc"); // 'desc' for newest first, 'asc' for oldest first
  const [statusFilter, setStatusFilter] = useState("active"); // New state for Active/Void filter
  const userRole = localStorage.getItem("userRole");
  // AlertDialog state
  const [dialog, setDialog] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setDialog({
            open: true,
            type: "error",
            title: "Authentication Error",
            message: "No authentication token found. Please log in.",
            onConfirm: () => {
              setDialog((prev) => ({ ...prev, open: false }));
              navigate("/login");
            },
          });
          return;
        }
        if (project.version === 2) {
          const [vendorsResponse, materialOrdersResponse] = await Promise.all([
            axios.get(`${API}/accounts/vendors`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${API}/materialOrders2/project/${id}`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);
          setVendors(vendorsResponse.data);
          const sortedOrders = materialOrdersResponse.data.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
          );
          setMaterialOrders(sortedOrders);
        } else {
          const [vendorsResponse, materialOrdersResponse] = await Promise.all([
            axios.get(
              "https://erp-production-72da01c8e651.herokuapp.com/api/accounts/vendors",
              { headers: { Authorization: `Bearer ${token}` } },
            ),
            axios.get(
              `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/project/${id}`,
              { headers: { Authorization: `Bearer ${token}` } },
            ),
          ]);
          setVendors(vendorsResponse.data);
          const sortedOrders = materialOrdersResponse.data.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
          );
          setMaterialOrders(sortedOrders);
        }
      } catch (err) {
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message:
            "Failed to load data: " +
            (err.response?.data?.message || err.message),
          onConfirm: () => {
            setDialog((prev) => ({ ...prev, open: false }));
            if (err.response?.status === 401) {
              navigate("/login");
            }
          },
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handleOrderMaterial = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setDialog({
        open: true,
        type: "error",
        title: "Authentication Error",
        message: "Please log in to order materials.",
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          navigate("/login");
        },
      });
      return;
    }
    if (project.version === 2) {
      navigate(`/projects/${id}/material-order2`);
    } else {
      navigate(`/projects/${id}/material-order`);
    }
  };

  const calculateCoverageFromSize = (size, uom = "") => {
    if (!size || typeof size !== "string") return 1;

    const trimmed = size.trim();
    if (!trimmed || trimmed === "" || trimmed === "N/A" || trimmed === "-")
      return 1;

    const parseDimensionToFeet = (dimStr) => {
      const s = dimStr.trim();
      const mixedMatch = s.match(
        /^(\d+(?:\.\d+)?)\s*'?\s*(\d+(?:\.\d+)?)?\s*"?$/i,
      );
      if (mixedMatch) {
        const feet = parseFloat(mixedMatch[1]) || 0;
        const inches = parseFloat(mixedMatch[2] || 0);
        return feet + inches / 12;
      }
      if (s.endsWith("'") && !s.includes('"')) {
        const num = parseFloat(s.slice(0, -1));
        return isNaN(num) ? 0 : num;
      }
      if (s.endsWith('"')) {
        const num = parseFloat(s.slice(0, -1));
        return isNaN(num) ? 0 : num / 12;
      }
      const num = parseFloat(s);
      return !isNaN(num) && num > 0 ? num : 0;
    };

    const normalized = trimmed.replace(/×/g, "x").replace(/\s+/g, " ").trim();

    const parts = normalized.split(/\s*[xX]\s*/);

    if (parts.length >= 2) {
      const width = parseDimensionToFeet(parts[0]);
      const height = parseDimensionToFeet(parts[1]);
      if (width > 0 && height > 0) {
        return Number((width * height).toFixed(4));
      }
    }

    const uomLower = (uom || "").toLowerCase();
    if (uomLower.includes("lf") || uomLower.includes("linear")) {
      const length = parseDimensionToFeet(normalized);
      if (length > 0) return Number(length.toFixed(4));
    }

    return 1;
  };

  const getCoverage = (item) => {
    if (!item) return 1;

    const uom = (item.uom || item.container || item.unit || "").toLowerCase();
    const isLFOrSF =
      uom === "lf" ||
      uom === "sf" ||
      uom.includes("linear") ||
      uom.includes("square");

    // If not LF or SF, coverage is always 1
    if (!isLFOrSF) return 1;

    // Priority 1: Use coveragePerUnit stored on the MO item
    if (
      item.coveragePerUnit != null &&
      item.coveragePerUnit !== "" &&
      !isNaN(parseFloat(item.coveragePerUnit))
    ) {
      const cov = parseFloat(item.coveragePerUnit);
      if (cov > 0) return cov;
    }

    // Priority 2: Use coverage from the linked budget line item
    if (
      item.lineItem?.coveragePerUnit != null &&
      item.lineItem?.coveragePerUnit !== "" &&
      !isNaN(parseFloat(item.lineItem.coveragePerUnit))
    ) {
      const originalCov = parseFloat(item.lineItem.coveragePerUnit);
      if (originalCov > 0) return originalCov;
    }

    // Priority 3: Calculate from size
    const currentSize = item.size || item.originalSize || "";
    const calculated = calculateCoverageFromSize(currentSize, uom);
    if (calculated > 0 && calculated !== 1)
      return Number(calculated.toFixed(4));

    return 1;
  };

  const isLinearOrSquareFootItem = (item) => {
    const size = (item.size || "").trim();
    const coverage = parseFloat(item.coveragePerUnit) || 0;

    // Check if it's a simple linear measurement like "10'" or "12'"
    const linearMatch = size.match(/^(\d+(?:\.\d+)?)\s*'$/);

    if (linearMatch) {
      const feet = parseFloat(linearMatch[1]);
      // If the size in feet roughly matches the coverage, it's a true LF item
      // Allow some tolerance for rounding
      return Math.abs(feet - coverage) < 0.5;
    }

    // Check for dimensional measurements like "4' x 8'" with coverage
    if (/\d+\s*['"]?\s*[xX×]\s*\d+\s*['"]?/.test(size) && coverage > 1) {
      return true;
    }

    return false;
  };

  const handleExportProcessed = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setDialog({
        open: true,
        type: "error",
        title: "Authentication Error",
        message: "Please log in to export orders.",
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          navigate("/login");
        },
      });
      return;
    }

    setIsExporting(true);
    try {
      const response = await fetch(
        `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/export-processed/${id}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) {
        // Try to parse JSON error body
        const err = await response.json().catch(() => ({
          message: `Server error ${response.status}`,
        }));
        throw new Error(err.message || "Export failed");
      }

      // Stream the ZIP blob and trigger a download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ProcessedMOs_${id}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Export Failed",
        message: err.message || "Could not export processed orders.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsExporting(false);
    }
  };

  const calculateOrderTotal = (order) => {
    if (!order?.items?.length) return "0.00";

    const safeNumber = (val, def = 0) => {
      const num = parseFloat(val);
      return isNaN(num) ? def : num;
    };

    const itemsTotal = order.items.reduce((sum, item) => {
      const qty = safeNumber(item.quantity, 0);
      const price = safeNumber(item.price || item.unitPrice, 0);
      const coverage = getCoverage(item);
      return sum + qty * coverage * price;
    }, 0);

    const surchargesTotal = (order.surcharges || []).reduce((sum, s) => {
      return sum + safeNumber(s.amount || s.price || s.value || 0);
    }, 0);

    return (itemsTotal + surchargesTotal).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleViewOrder = (order) => {
    if (order.isStockOut) {
      navigate(`/projects/${id}/stock-out-order/${order._id}`);
    } else if (project.version === 2) {
      navigate(`/projects/${id}/material-order2/${order._id}`);
    } else {
      navigate(`/projects/${id}/material-order/${order._id}`);
    }
  };
  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    setMaterialOrders((prevOrders) =>
      [...prevOrders].sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return sortOrder === "desc" ? dateA - dateB : dateB - dateA;
      }),
    );
  };

  const handleOrderChange = (e) => {
    const { name, value } = e.target;
    setOrderData({ ...orderData, [name]: value });
  };

  const fetchNextMaterialOrderNumber = async (poNumber) => {
    const token = localStorage.getItem("token");

    if (project.version === 2) {
      const response = await fetch(
        "https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders2/next-number",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        const text = await response.text();
        throw new Error(
          `Failed to fetch material order number: ${response.status} - ${text}`,
        );
      }
      const { nextNumber } = await response.json();
      return `${poNumber}-${nextNumber}`;
    } else {
      const response = await fetch(
        "https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/next-number",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        const text = await response.text();
        throw new Error(
          `Failed to fetch material order number: ${response.status} - ${text}`,
        );
      }
      const { nextNumber } = await response.json();
      return `${poNumber}-${nextNumber}`;
    }
  };

  const handlePOChange = async (selected) => {
    const poId = selected ? selected.value : "";
    const po = project?.purchaseOrders.find((p) => p._id === poId);
    try {
      const materialOrderNumber = poId
        ? await fetchNextMaterialOrderNumber(po.poNumber.toString())
        : "";
      setOrderData({
        ...orderData,
        purchaseOrder: poId,
        poNumber: po ? po.poNumber.toString() : "",
        vendor: "",
        materialOrderNumber,
        items: [],
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to generate material order number: " + err.message,
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      console.error("PO change error:", err);
    }
  };

  const handleItemSelect = (itemId) => {
    setOrderData((prev) => {
      const item = filteredItems.find((i) => i._id === itemId);
      const maxOrderable =
        item.quantityRequired - item.quantityOrdered - item.quantityPending;
      if (prev.items.some((i) => i.itemId === itemId)) {
        return {
          ...prev,
          items: prev.items.filter((i) => i.itemId !== itemId),
        };
      } else {
        return {
          ...prev,
          items: [
            ...prev.items,
            {
              itemId,
              quantity: Math.min(1, maxOrderable),
              bidArea: item.bidArea || "N/A",
            },
          ],
        };
      }
    });
  };

  const handleQuantityChange = (itemId, value) => {
    const item = filteredItems.find((i) => i._id === itemId);
    const maxOrderable =
      item.quantityRequired - item.quantityOrdered - item.quantityPending;
    const quantity = Math.min(Math.max(0, parseInt(value) || 0), maxOrderable);
    setOrderData((prev) => ({
      ...prev,
      items: prev.items.map((i) =>
        i.itemId === itemId ? { ...i, quantity } : i,
      ),
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
      file,
    }));
    setOrderData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments],
    }));
  };

  const getUserIdFromToken = () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        return decoded.id;
      } catch (err) {
        console.error("Error decoding token:", err);
        return null;
      }
    }
    return null;
  };

  const shouldUseCoverage = (item) => {
    const uom = (item.uom || "").toLowerCase();
    return uom.includes("sf") || uom.includes("lf");
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    if (!token) {
      setDialog({
        open: true,
        type: "error",
        title: "Authentication Error",
        message: "Please log in to submit an order.",
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          navigate("/login");
        },
      });
      return;
    }

    if (
      !orderData.purchaseOrder ||
      !orderData.vendor ||
      !orderData.poNumber ||
      !orderData.materialOrderNumber ||
      orderData.items.length === 0
    ) {
      setDialog({
        open: true,
        type: "error",
        title: "Validation Error",
        message:
          "PO, Vendor, PO Number, Material Order Number, and at least one item are required.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    if (orderData.items.some((i) => i.quantity <= 0)) {
      setDialog({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "All selected items must have a quantity greater than 0.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    try {
      const payload = {
        project: project._id,
        purchaseOrder: orderData.purchaseOrder,
        vendor: orderData.vendor,
        poNumber: orderData.poNumber,
        materialOrderNumber: orderData.materialOrderNumber,
        items: orderData.items.map((item) => ({
          projectItem: item.itemId,
          size: filteredItems.find((i) => i._id === item.itemId)?.size || 0,
          quantity: item.quantity,
          bidArea: item.bidArea,
          price:
            filteredItems.find((i) => i._id === item.itemId)?.unitCost || 0,
        })),
        shippingMethod: orderData.shippingMethod,
        deliveryInstructions: orderData.deliveryInstructions || "",
        createdBy: getUserIdFromToken() || "",
        status: "pending",
      };

      const response = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const { materialOrder, project: updatedProject } = response.data;

      setProject(updatedProject);
      setMaterialOrders((prev) => {
        const updatedOrders = [...prev, materialOrder].sort((a, b) =>
          sortOrder === "desc"
            ? new Date(b.createdAt) - new Date(a.createdAt)
            : new Date(a.createdAt) - new Date(b.createdAt),
        );
        return updatedOrders;
      });
      setShowOrderForm(false);
      setOrderData({
        purchaseOrder: "",
        vendor: "",
        poNumber: "",
        items: [],
        shippingMethod: "Delivery",
        deliveryInstructions: "",
        attachments: [],
        materialOrderNumber: "",
      });
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Material order created successfully!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to create material order: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      console.error("Submit error:", err);
    }
  };

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: vendor.companyName,
  }));

  const poOptions =
    project?.purchaseOrders?.map((po) => ({
      value: po._id,
      label: `${po.title} (PO# ${po.poNumber})`,
    })) || [];

  const filteredItems =
    project?.projectItems?.filter((item) => {
      if (!orderData.purchaseOrder) return !item.linkedPO;
      return item.linkedPO === orderData.purchaseOrder;
    }) || [];

  // Filter orders based on statusFilter
  const filteredOrders = materialOrders.filter((order) =>
    statusFilter === "active"
      ? order.status !== "void"
      : order.status === "void",
  );

  if (isLoading || !project)
    return (
      <div className="text-center py-4 text-gray-200 bg-gray-700">
        Loading...
      </div>
    );

  return (
    <div className="p-4 bg-gray-700 rounded-md">
      <AlertDialog
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText="OK"
        cancelText="Close"
        open={dialog.open}
        onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={dialog.onConfirm}
      />

      {/* Tab Navigation */}
      <div className="flex justify-between items-center border-b border-gray-600 mb-4">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("orders")}
            className={`${
              activeTab === "orders"
                ? "border-orange-400 text-white bg-gray-600"
                : "border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-600"
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm rounded-t-md transition-colors duration-200`}
          >
            Orders
          </button>
          {project.version !== 2 ? (
            <button
              onClick={() => setActiveTab("materials")}
              className={`${
                activeTab === "materials"
                  ? "border-orange-400 text-white bg-gray-600"
                  : "border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-600"
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm rounded-t-md transition-colors duration-200`}
            >
              Materials
            </button>
          ) : (
            <></>
          )}
        </nav>

        <div className="pb-2 flex items-center gap-2">
          {userRole !== "foreman" && (
            <button
              onClick={handleExportProcessed}
              disabled={isExporting}
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2"
            >
              {isExporting ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Exporting…
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Export Processed
                </>
              )}
            </button>
          )}
          <button
            onClick={handleOrderMaterial}
            className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
          >
            Generate Material Order
          </button>
          <button
            onClick={() => navigate(`/projects/${id}/stock-out-order`)}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-500 
                rounded-md hover:bg-purple-600 transition-colors"
          >
            Stock Out
          </button>
        </div>
      </div>

      {/* Orders View */}
      {activeTab === "orders" && (
        <div className="overflow-x-auto rounded-lg shadow-xl">
          <div className="flex justify-between items-center mb-4">
            <div className="w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              >
                <option value="active">Active Orders</option>
                <option value="void">Void Orders</option>
              </select>
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center">
                    Created
                    <button
                      onClick={handleSortToggle}
                      className="ml-2 focus:outline-none text-gray-400 hover:text-gray-200"
                    >
                      <svg
                        className={`w-4 h-4 ${
                          sortOrder === "desc" ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Material Order #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  PO Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Shipping Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Bill Status
                </th>

                {userRole === "foreman" ? (
                  ""
                ) : (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Total Price
                  </th>
                )}

                <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider actions-column">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td
                    colSpan="9"
                    className="px-6 py-4 text-sm text-gray-400 text-center"
                  >
                    No {statusFilter === "active" ? "active" : "void"} material
                    orders found.
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr
                    key={order._id}
                    onClick={(e) => {
                      if (e.target.closest(".actions-column")) return;
                      handleViewOrder(order);
                    }}
                    className="hover:bg-gray-500 cursor-pointer transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      {new Date(order.createdAt).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      {order.materialOrderNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      {order.poNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      {order.vendor?.companyName || "Unknown"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      {order.shippingMethod}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          order.status === "pending"
                            ? "bg-yellow-600/20 text-yellow-400"
                            : order.status === "delivered"
                              ? "bg-green-600/20 text-green-400"
                              : "bg-red-600/20 text-red-400"
                        }`}
                      >
                        {order.status.charAt(0).toUpperCase() +
                          order.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          order.processed
                            ? "bg-green-600/20 text-green-400"
                            : "bg-yellow-600/20 text-yellow-400"
                        }`}
                      >
                        {order.processed ? "Processed" : "Pending"}
                      </span>
                    </td>
                    {userRole === "foreman" ? (
                      ""
                    ) : (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-right">
                        ${calculateOrderTotal(order)}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right actions-column">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewOrder(order);
                        }}
                        className="text-orange-400 hover:text-orange-300"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Cost Summary */}
          {userRole !== "foreman" &&
            (() => {
              const pendingCost = filteredOrders
                .filter((o) => o.status !== "delivered")
                .reduce(
                  (sum, o) =>
                    sum + parseFloat(calculateOrderTotal(o).replace(/,/g, "")),
                  0,
                );
              const actualCost = filteredOrders
                .filter((o) => o.status === "delivered")
                .reduce(
                  (sum, o) =>
                    sum + parseFloat(calculateOrderTotal(o).replace(/,/g, "")),
                  0,
                );
              const totalCost = pendingCost + actualCost;
              const colSpan = 7; // columns before Total Price
              return (
                <table className="min-w-full bg-gray-800 border-t-2 border-gray-500">
                  <tbody>
                    <tr className="border-b border-gray-600">
                      <td
                        colSpan={colSpan}
                        className="px-6 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Pending Material Cost
                      </td>
                      <td className="px-6 py-2 text-sm text-yellow-400 font-medium text-right">
                        $
                        {pendingCost.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td />
                    </tr>
                    <tr className="border-b border-gray-600">
                      <td
                        colSpan={colSpan}
                        className="px-6 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Delivered Material Cost
                      </td>
                      <td className="px-6 py-2 text-sm text-green-400 font-medium text-right">
                        $
                        {actualCost.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td />
                    </tr>
                    <tr>
                      <td
                        colSpan={colSpan}
                        className="px-6 py-3 text-xs font-semibold text-gray-300 uppercase tracking-wider"
                      >
                        Total
                      </td>
                      <td className="px-6 py-3 text-sm text-orange-400 font-semibold text-right">
                        $
                        {totalCost.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              );
            })()}
        </div>
      )}

      {/* Materials View */}

      {/* Materials View */}
      {activeTab === "materials" && (
        <div className="overflow-x-auto rounded-lg shadow-xl">
          <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Item #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Container
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Quantity Required
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Quantity Ordered
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Quantity Pending
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Bid Area
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Linked PO
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {(project.projectItems || []).map((item, index) => (
                <tr
                  key={item._id}
                  className={
                    index % 2 === 0
                      ? "bg-gray-700 hover:bg-gray-500 transition-colors duration-200"
                      : "bg-gray-600 hover:bg-gray-500 transition-colors duration-200"
                  }
                >
                  <td className="px-6 py-4 text-sm text-gray-200">{index}</td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {item.title}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {item.description}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {item.size || "N/A"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {item.container || "N/A"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {item.quantityRequired}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {item.quantityOrdered}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {item.quantityPending}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {item.bidArea || "N/A"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {item.linkedPO
                      ? project.purchaseOrders.find(
                          (po) => po._id === item.linkedPO,
                        )?.title || "Unknown"
                      : "None"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Material Order Form */}
      {showOrderForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-50 z-50">
          <div className="bg-gray-700 p-6 rounded-lg shadow-lg max-w-lg w-full border border-gray-600">
            <h3 className="text-lg font-semibold text-orange-400 mb-4">
              Order Material
            </h3>
            <form onSubmit={handleOrderSubmit} className="space-y-4">
              <Select
                options={poOptions}
                value={
                  poOptions.find(
                    (option) => option.value === orderData.purchaseOrder,
                  ) || null
                }
                onChange={handlePOChange}
                placeholder="Select Purchase Order"
                className="mt-1"
                styles={customStyles}
                required
              />
              <Select
                options={vendorOptions}
                value={
                  vendorOptions.find(
                    (option) => option.value === orderData.vendor,
                  ) || null
                }
                onChange={(selected) =>
                  setOrderData({
                    ...orderData,
                    vendor: selected ? selected.value : "",
                  })
                }
                placeholder="Select Vendor"
                className="mt-1"
                styles={customStyles}
                required
              />
              <input
                type="text"
                name="poNumber"
                value={orderData.poNumber}
                onChange={handleOrderChange}
                placeholder="PO Number"
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                required
              />
              <input
                type="text"
                name="materialOrderNumber"
                value={orderData.materialOrderNumber}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm sm:text-sm"
              />
              <div>
                <h4 className="text-sm font-medium text-gray-400">
                  Select Materials
                </h4>
                {filteredItems.map((item) => {
                  const selectedItem = orderData.items.find(
                    (i) => i.itemId === item._id,
                  );
                  const maxOrderable =
                    item.quantityRequired -
                    item.quantityOrdered -
                    item.quantityPending;
                  return (
                    <div
                      key={item._id}
                      className="flex items-center space-x-2 text-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={!!selectedItem}
                        onChange={() => handleItemSelect(item._id)}
                        className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                      />
                      <span>{item.title}</span>
                      {selectedItem && (
                        <input
                          type="number"
                          value={selectedItem.quantity}
                          onChange={(e) =>
                            handleQuantityChange(item._id, e.target.value)
                          }
                          min="0"
                          max={maxOrderable}
                          className="w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      )}
                      <span>
                        (Req: {item.quantityRequired}, Ord:{" "}
                        {item.quantityOrdered}, Pend: {item.quantityPending})
                      </span>
                    </div>
                  );
                })}
              </div>
              <select
                name="shippingMethod"
                value={orderData.shippingMethod}
                onChange={handleOrderChange}
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              >
                <option value="Delivery">Delivery</option>
                <option value="Pickup">Pickup</option>
              </select>
              <textarea
                name="deliveryInstructions"
                value={orderData.deliveryInstructions}
                onChange={handleOrderChange}
                placeholder="Delivery Instructions"
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              />
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="mt-1 block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-gray-200 hover:file:bg-gray-500"
                accept="image/*,.pdf"
              />
              {orderData.attachments.map((att, idx) => (
                <div key={idx} className="text-sm text-gray-200">
                  {att.name}
                </div>
              ))}
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowOrderForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                >
                  Save Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
